// File: frontend/src/components/entries/EntryPostForm.tsx
import React, { useState, useRef, useEffect } from "react";
import { MatchModel } from "../../models";
import "./EntryPostForm.css";
import { useTranslation } from "react-i18next";
import { TOAST_DURATION_NORMAL } from "../../constants";
import { RateLimitError, DuplicateEntryError } from "../../services/apiClient";
import { MdKeyboard } from "react-icons/md";
import { RiDeleteBin6Line, RiErrorWarningLine, RiCloseLine } from "react-icons/ri";
import { FaRegSave } from "react-icons/fa";
import { EntryEditorToolbar } from "./EntryEditorToolbar";
import { ChipEditor, ChipEditorHandle } from "./ChipEditor";
import { SpoilerBlock } from "./SpoilerBlock";
import { EntryImage, collectEntryImageUrls, normalizeImageTokenSpacing } from "./EntryImage";
import { renderBasicMarkup } from "../../utils/inlineMarkup";
import { findBlockToken } from "../../utils/markupUtils";
import { createDraft } from "../../services/draftService";
import { parseApiError } from "../../utils/parseApiError";
import { lowercaseEntryContent } from "../../utils/entryContentUtils";
// Entry length rules come from the admin panel, not from a compile-time constant
import { useSiteSettings } from "../../context/SiteSettingsContext";

interface EntryPostFormProps {
  selectedMatch: MatchModel | null;
  onPostEntry: (content: string) => Promise<void>;
  // When personId is set, match-specific features (drafts, match guard) are disabled
  personId?: number | null;
  // When clubId or teamId is set, match-specific features are also disabled
  clubId?: number | null;
  teamId?: number | null;
  // When customTopicId is set, match-specific features are disabled (drafts are supported)
  customTopicId?: number | null;
  // When true, the topic is locked by an admin/mod — entry form is replaced with a warning
  isLocked?: boolean;
  // Admin's note explaining the lock, shown to the user only when the admin marked it visible.
  // Null/undefined means show the generic locked message instead.
  lockReason?: string | null;
}

// Regex patterns to detect HTML tags and common XSS patterns
const HTML_TAG_PATTERN = /<\s*\/?[a-zA-Z][^>]*>/;
const JS_PROTOCOL_PATTERN = /javascript\s*:/i;
const EVENT_HANDLER_PATTERN = /on[a-zA-Z]+\s*=/i;

/**
 * Returns true if the given string contains HTML tags or XSS attack patterns.
 */
function containsHtml(value: string): boolean {
  return (
    HTML_TAG_PATTERN.test(value) ||
    JS_PROTOCOL_PATTERN.test(value) ||
    EVENT_HANDLER_PATTERN.test(value)
  );
}

// Turkish special characters shown in the keyboard popup.
// Entry content is always lowercase (sözlük convention), so only the lowercase
// variants are offered here. The messaging keyboard (ConversationView) keeps
// both cases because private messages are not lowercased.
const TURKISH_CHARS = ["ç", "ğ", "ı", "ö", "ş", "ü"];


// ─── Preview renderer ─────────────────────────────────────────────────────────

// Current match ref format: (mac: label||matchId)
const PREVIEW_MAC_REF = /\(mac:\s*(.*?)\|\|(\d+)\)/g;
// Legacy match ref format: (ref: label||matchId) — backward compat
const PREVIEW_REF_NEW = /\(ref:\s*(.*?)\|\|(\d+)\)/g;
const PREVIEW_REF_LEGACY = /\((ref|bkz):\s*([^\s)]+)\)/g;
const PREVIEW_BOLD = /\(b:\s*(.*?)\)/g;
const PREVIEW_ITALIC = /\(i:\s*(.*?)\)/g;
const PREVIEW_LINK = /\(link:\s*([^|)]+)\|([^)]+)\)/g;
const PREVIEW_ENTRY_REF = /\(#(\d+)\)/g;
// Person reference preview pattern — mirrors PERSON_REF_PATTERN in EntryRow
const PREVIEW_PERSON_REF = /\(kisi:\s*(.*?)\|\|([^)]+)\)/g;
// Club reference preview pattern — mirrors CLUB_REF_PATTERN in EntryRow
const PREVIEW_CLUB_REF = /\(kulup:\s*(.*?)\|\|([^)]+)\)/g;
// Team reference preview pattern — mirrors TEAM_REF_PATTERN in EntryRow
const PREVIEW_TEAM_REF = /\(takim:\s*(.*?)\|\|([^)\s]+)\)/g;
// Custom topic reference preview: (baslik: title||slug)
const PREVIEW_TOPIC_REF = /\(baslik:\s*(.*?)\|\|([^)]+)\)/g;
// Inline image: (gorsel: <ref>) — mirrors GORSEL_PATTERN in EntryRow.
// <ref> is an opaque image id (new format) or a full URL (legacy entries).
const PREVIEW_GORSEL = /\(gorsel:\s*(https?:\/\/[^)\s]+|[a-f0-9]{32}\.(?:jpg|jpeg|png|webp))\s*\)/g;
// Spoiler/alinti block previews: (spoiler: text) / (alinti: text) — located
// with findBlockToken() (markupUtils.tsx), which mirrors EntryRow's handling.
// A single regex can't reliably find the matching closing parenthesis once
// the pasted content contains its own "(" / ")" characters.
// "(bkz: ...)" reference variants — mirror the BKZ_*_PATTERN set in EntryRow
const PREVIEW_BKZ_REFS: Array<{ pattern: RegExp; keyPrefix: string }> = [
  { pattern: /\(bkz-mac:\s*(.*?)\|\|(\d+)\)/g, keyPrefix: "bkz-mac" },
  { pattern: /\(bkz-kisi:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "bkz-kisi" },
  { pattern: /\(bkz-kulup:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "bkz-kulup" },
  { pattern: /\(bkz-takim:\s*(.*?)\|\|([^)\s]+)\)/g, keyPrefix: "bkz-takim" },
  { pattern: /\(bkz-baslik:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "bkz-baslik" },
];

/**
 * Render entry content markup into React nodes for the preview modal.
 * Mirrors EntryRow renderContent — bold, italic, ref links, external links.
 */
function renderPreview(text: string): React.ReactNode[] {
  interface Span { index: number; length: number; node: React.ReactNode; }
  const spans: Span[] = [];
  // Same image-token line-break cleanup as EntryRow's renderContent.
  text = normalizeImageTokenSpacing(text);
  // Images of the draft being previewed — shared with every EntryImage so the
  // lightbox can navigate between them, exactly as on the posted entry.
  const galleryUrls = collectEntryImageUrls(text);
  let m: RegExpExecArray | null;

  // Current match ref format (mac: label||matchId)
  PREVIEW_MAC_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_MAC_REF.exec(text)) !== null) {
    const label = m[1].trim();
    spans.push({ index: m.index, length: m[0].length,
      node: <span key={`mac-${m.index}`} className="epf-preview-ref">{label}</span> });
  }

  // Legacy match ref format (ref: label||matchId) — backward compat
  PREVIEW_REF_NEW.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_REF_NEW.exec(text)) !== null) {
    const label = m[1].trim();
    spans.push({ index: m.index, length: m[0].length,
      node: <span key={`ref-${m.index}`} className="epf-preview-ref">{label}</span> });
  }

  PREVIEW_REF_LEGACY.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_REF_LEGACY.exec(text)) !== null) {
    if (spans.some(s => s.index === m!.index)) continue;
    const label = m[2].replace(/--\d+$/, "").replace(/-/g, " ");
    spans.push({ index: m.index, length: m[0].length,
      node: <span key={`legacy-${m.index}`} className="epf-preview-ref">{label}</span> });
  }

  PREVIEW_BOLD.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_BOLD.exec(text)) !== null) {
    spans.push({ index: m.index, length: m[0].length,
      node: <strong key={`b-${m.index}`}>{m[1]}</strong> });
  }

  PREVIEW_ITALIC.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_ITALIC.exec(text)) !== null) {
    spans.push({ index: m.index, length: m[0].length,
      node: <em key={`i-${m.index}`}>{m[1]}</em> });
  }

  PREVIEW_LINK.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_LINK.exec(text)) !== null) {
    const url = m[1].trim();
    const display = m[2].trim();
    spans.push({ index: m.index, length: m[0].length,
      node: <span key={`link-${m.index}`} className="epf-preview-link" title={url}>{display} <span className="epf-preview-link-arrow">↗</span></span> });
  }

  PREVIEW_ENTRY_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_ENTRY_REF.exec(text)) !== null) {
    const entryId = m[1];
    spans.push({ index: m.index, length: m[0].length,
      node: <span key={`eref-${m.index}`} className="epf-preview-entry-ref">#{entryId}</span> });
  }

  PREVIEW_PERSON_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_PERSON_REF.exec(text)) !== null) {
    const label = m[1].trim();
    spans.push({ index: m.index, length: m[0].length,
      node: <span key={`kisi-${m.index}`} className="epf-preview-ref">{label}</span> });
  }

  PREVIEW_CLUB_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_CLUB_REF.exec(text)) !== null) {
    const label = m[1].trim();
    spans.push({ index: m.index, length: m[0].length,
      node: <span key={`kulup-${m.index}`} className="epf-preview-ref">{label}</span> });
  }

  PREVIEW_TEAM_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_TEAM_REF.exec(text)) !== null) {
    const label = m[1].trim();
    spans.push({ index: m.index, length: m[0].length,
      node: <span key={`takim-${m.index}`} className="epf-preview-ref">{label}</span> });
  }

  PREVIEW_TOPIC_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_TOPIC_REF.exec(text)) !== null) {
    const label = m[1].trim();
    spans.push({ index: m.index, length: m[0].length,
      node: <span key={`baslik-${m.index}`} className="epf-preview-ref">{label}</span> });
  }

  // Spoiler block — collapsed by default, click to reveal
  // eslint-disable-next-line no-loop-func
  for (let block = findBlockToken(text, "spoiler", 0); block; block = findBlockToken(text, "spoiler", block.index + block.length)) {
    const hidden = block.content.trim();
    spans.push({ index: block.index, length: block.length,
      node: <SpoilerBlock key={`spoiler-${block.index}`} text={hidden} /> });
  }

  // Quote block — faded background, italic, wrapped in quotes
  // eslint-disable-next-line no-loop-func
  for (let block = findBlockToken(text, "alinti", 0); block; block = findBlockToken(text, "alinti", block.index + block.length)) {
    const quoted = block.content.trim();
    spans.push({ index: block.index, length: block.length,
      node: (
        <blockquote key={`alinti-${block.index}`} className="entry-quote-block">
          {"\u201C"}{renderBasicMarkup(quoted, galleryUrls)}{"\u201D"}
        </blockquote>
      ) });
  }

  // "(bkz: ...)" variants — label styled like inline refs, wrapped in literal parens
  for (const v of PREVIEW_BKZ_REFS) {
    v.pattern.lastIndex = 0;
    // eslint-disable-next-line no-loop-func
    while ((m = v.pattern.exec(text)) !== null) {
      const label = m[1].trim();
      spans.push({ index: m.index, length: m[0].length,
        node: (
          <span key={`${v.keyPrefix}-${m.index}`} className="epf-preview-bkz">
            {"(bkz: "}<span className="epf-preview-ref">{label}</span>{")"}
          </span>
        ) });
    }
  }

  PREVIEW_GORSEL.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_GORSEL.exec(text)) !== null) {
    const url = m[1].trim();
    spans.push({ index: m.index, length: m[0].length,
      node: (
        <EntryImage key={`gorsel-${m.index}`} src={url} gallery={galleryUrls} />
      ),
    });
  }

  spans.sort((a, b) => a.index - b.index);

  // Drop spans fully contained inside an earlier (outer) span — e.g. a (b: ...)
  // token pasted inside an (alinti: ...) quote must not render a second time.
  const filtered: Span[] = [];
  let coveredUntil = -1;
  for (const s of spans) {
    if (s.index < coveredUntil) continue;
    filtered.push(s);
    coveredUntil = s.index + s.length;
  }

  const nodes: React.ReactNode[] = [];
  let last = 0;
  for (const s of filtered) {
    if (s.index > last) nodes.push(text.slice(last, s.index));
    nodes.push(s.node);
    last = s.index + s.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

// ─── EntryPostForm ────────────────────────────────────────────────────────────

// Draft is stored per match so switching matches doesn't mix drafts.
// Legacy key format — kept unchanged so drafts already sitting in the browser
// of an existing user are still restored after this change.
const draftKey = (matchId: number) => `entry_draft_${matchId}`;

// The entry form can be bound to five different targets. The local autosave
// used to cover matches only, which is why typing on a person/club/team/topic
// page was lost as soon as the user navigated away.
type DraftTargetKind = "match" | "person" | "club" | "team" | "topic";

interface DraftTarget {
  kind: DraftTargetKind;
  id: number;
}

/**
 * localStorage key for the auto-saved draft of a given target.
 * Matches keep the legacy `entry_draft_<id>` key; the other domains get their
 * own namespace so a person with id 5 and a club with id 5 never collide.
 */
const draftStorageKey = (target: DraftTarget | null): string | null => {
  if (!target) return null;
  return target.kind === "match" ? draftKey(target.id) : `entry_draft_${target.kind}_${target.id}`;
};

export const EntryPostForm: React.FC<EntryPostFormProps> = ({
  selectedMatch,
  onPostEntry,
  personId = null,
  clubId = null,
  teamId = null,
  customTopicId = null,
  isLocked = false,
  lockReason = null,
}) => {
  // Entry form is active when any target is set: match, person, club, or team
  const hasTarget = !!(selectedMatch || personId || clubId || teamId || customTopicId);

  // Active target of this form — exactly one of the props is ever set at a time.
  // Used for the local (silent) autosave of unfinished content.
  const draftTarget: DraftTarget | null =
    selectedMatch   ? { kind: "match",  id: selectedMatch.id }
    : personId      ? { kind: "person", id: personId }
    : clubId        ? { kind: "club",   id: clubId }
    : teamId        ? { kind: "team",   id: teamId }
    : customTopicId ? { kind: "topic",  id: customTopicId }
    : null;
  const localDraftKey = draftStorageKey(draftTarget);

  const { t } = useTranslation();
  // Admin-configurable maximum entry length; updates as soon as the setting is
  // saved, so the counter and the textarea cap never disagree with the backend
  const { entryMaxLength } = useSiteSettings();

  // i18n key for the duplicate-content error, picked from the target this form
  // is bound to. Only one of the target props is ever set at a time.
  const duplicateErrorKey =
    personId      ? "entry_duplicate_error_person"
    : clubId      ? "entry_duplicate_error_club"
    : teamId      ? "entry_duplicate_error_team"
    : customTopicId ? "entry_duplicate_error_topic"
    : "entry_duplicate_error_match";
  const textareaRef = useRef<ChipEditorHandle>(null);

  // ── State declarations (must come before useEffects that reference them) ──
  const [content, setContent] = useState(() => {
    // Restore draft for this target on first render if one exists
    if (localDraftKey) {
      return localStorage.getItem(localDraftKey) || "";
    }
    return "";
  });
  const [isPosting, setIsPosting] = useState(false);
  // True while the toolbar is uploading an image — submitting now would post
  // the entry without the (gorsel: ...) token that is still on its way
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [error, setError] = useState("");
  const [showRateLimitToast, setShowRateLimitToast] = useState(false);
  const [showTrKeyboard, setShowTrKeyboard] = useState(false);
  // Wraps the keyboard toggle + Turkish character popup — used to close the
  // popup when the user clicks anywhere outside it (more user friendly than
  // requiring a second click on the toggle button).
  const trKeyboardRef = useRef<HTMLDivElement>(null);

  // Close the Turkish character keyboard on any outside click
  useEffect(() => {
    if (!showTrKeyboard) return;
    const onMouseDown = (e: MouseEvent) => {
      if (trKeyboardRef.current && !trKeyboardRef.current.contains(e.target as Node)) {
        setShowTrKeyboard(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [showTrKeyboard]);
  const [draftRestored, setDraftRestored] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // Sticky textarea expansion — set on first focus, only reset when the
  // content is cleared (post/clear), so the box never collapses on its own
  const [textareaExpanded, setTextareaExpanded] = useState(false);

  // No auto-grow effect is needed here. A textarea has a fixed height and had
  // to be resized in JS to avoid an inner scrollbar; ChipEditor is a div, so
  // it grows with its content on its own and the CSS only sets minimums.

  // Draft-save related state
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftSavedToast, setDraftSavedToast] = useState(false);

  // Restore the saved draft whenever the target changes (another match, person,
  // club, team or custom topic). Without a target the form is cleared.
  useEffect(() => {
    setShowPreview(false);
    setError("");
    if (localDraftKey) {
      const saved = localStorage.getItem(localDraftKey) || "";
      setContent(saved);
      setDraftRestored(!!saved.trim());
    } else {
      setContent("");
      setDraftRestored(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localDraftKey]);

  // Persist the draft to localStorage on every keystroke — silently, without
  // asking the user. Applies to every target domain, not just matches.
  useEffect(() => {
    if (!localDraftKey) return;
    if (content.trim()) {
      localStorage.setItem(localDraftKey, content);
    } else {
      localStorage.removeItem(localDraftKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, localDraftKey]);

  // Warn the user before closing/refreshing the tab when draft is not empty
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (content.trim()) {
        e.preventDefault();
        // Modern browsers show their own message; setting returnValue triggers the dialog
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [content]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim()) {
      setError(t("entry_form_empty_content_error"));
      return;
    }

    // Client-side HTML/XSS check — mirrors backend validation
    if (containsHtml(content)) {
      setError(t("entry_form_html_not_allowed_error"));
      return;
    }

    if (!hasTarget) {
      setError(t("entry_form_no_match_selected_error"));
      return;
    }

    try {
      setIsPosting(true);
      setError("");
      // Sözlük convention: entry content is posted lowercase (Turkish-aware).
      // URLs and reference-token ids/slugs are preserved by the normalizer.
      // The backend applies the same normalization as the source of truth.
      await onPostEntry(lowercaseEntryContent(content));
      setContent("");
      setShowPreview(false);
      setTextareaExpanded(false);
      // Clear saved draft after successful post
      if (localDraftKey) {
        localStorage.removeItem(localDraftKey);
      }
      setDraftRestored(false);
    } catch (err) {
      if (err instanceof RateLimitError) {
        // Daily-limit errors are also sent as 429 by the backend and therefore arrive
        // here as RateLimitError. Distinguish them by their structured message prefix
        // and show an inline translated error instead of the rate-limit toast.
        if (err.message.startsWith("ENTRY_DAILY_LIMIT_REACHED:")) {
          setError(parseApiError(err.message, t));
        } else {
          // True in-memory rate limit — show the toast
          setShowRateLimitToast(true);
          setTimeout(() => setShowRateLimitToast(false), TOAST_DURATION_NORMAL);
        }
      } else if (err instanceof DuplicateEntryError) {
        // Show translated duplicate error message inline (same style as other form errors).
        // The wording names the domain the form is posting to — a club page saying
        // "already submitted for this match" was simply wrong.
        setError(t(duplicateErrorKey));
      } else {
        // All other backend errors (ENTRY_TOO_SHORT, ENTRY_TOO_LONG, etc.) — parse
        // the structured error code and display a translated inline message.
        const msg = err instanceof Error ? err.message : "";
        setError(parseApiError(msg, t));
      }
    } finally {
      setIsPosting(false);
    }
  };

  /**
   * Save the current textarea content as a persistent backend draft.
   * Works for match, person, club, and team targets.
   * On success the form is cleared (the user can then write another entry or navigate
   * to the drafts page) and a short toast is shown.
   * On failure the content is preserved and an inline error is shown.
   */
  const handleSaveDraft = async () => {
    if (!content.trim()) {
      setError(t("entry_form_empty_content_error"));
      return;
    }
    if (containsHtml(content)) {
      setError(t("entry_form_html_not_allowed_error"));
      return;
    }
    // Guard: at least one target must be set
    if (!hasTarget) return;

    try {
      setIsSavingDraft(true);
      setError("");

      // Send whichever target is active — exactly one will be non-null
      await createDraft({
        content,
        matchId:       selectedMatch ? selectedMatch.id : null,
        personId:      personId      ?? null,
        clubId:        clubId        ?? null,
        teamId:        teamId        ?? null,
        customTopicId: customTopicId ?? null,
      });

      // Clear the textarea and any per-target localStorage draft so the user
      // starts fresh — the content now lives on the server as a draft.
      setContent("");
      setShowPreview(false);
      if (localDraftKey) {
        localStorage.removeItem(localDraftKey);
      }
      setDraftRestored(false);

      // Show a brief confirmation toast
      setDraftSavedToast(true);
      setTimeout(() => setDraftSavedToast(false), TOAST_DURATION_NORMAL);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setError(parseApiError(msg, t));
    } finally {
      setIsSavingDraft(false);
    }
  };

  /**
   * Insert a Turkish character at the current cursor position in the textarea.
   */
  const insertTurkishChar = (char: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent = content.slice(0, start) + char + content.slice(end);
    setContent(newContent);
    requestAnimationFrame(() => {
      textarea.selectionStart = start + char.length;
      textarea.selectionEnd = start + char.length;
      textarea.focus();
    });
  };

  const isNearLimit = entryMaxLength - content.length <= 50;

  // When the topic is locked, replace the entire form with a warning banner
  if (isLocked) {
    return (
      <div className="entry-post-form">
        <div className="epf-topic-locked-banner">
          <span className="epf-topic-locked-icon">🔒</span>
          <div>
            <div>{t("entry_topic_locked")}</div>
            {lockReason && (
              <div className="epf-topic-locked-reason">
                {t("entry_topic_locked_reason_prefix")} {lockReason}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="entry-post-form">
      <form onSubmit={handleSubmit}>
        {/* ── Toolbar row (shared component) + character counter ── */}
        <div className="epf-toolbar">
          <EntryEditorToolbar
            textareaRef={textareaRef}
            content={content}
            setContent={setContent}
            disabled={!hasTarget}
            onUploadingChange={setIsImageUploading}
          />
          {/* Character counter — top right */}
          <span className={`epf-char-counter${isNearLimit ? " epf-char-counter--warn" : ""}`}>
            {content.length} / {entryMaxLength}
          </span>
        </div>

        {/* Error message — between toolbar and textarea. Shared by every entry
            form error (too short, daily limit, duplicate, consecutive gündem
            entry, ...) on all five domains. Uses its own "epf-error" class: the
            generic ".error-message" class is also styled globally by
            AdminUsers.css, whose large padding leaked into this form. */}
        {error && (
          <div className="epf-error" role="alert">
            <RiErrorWarningLine className="epf-error__icon" aria-hidden="true" />
            <span className="epf-error__text">{error}</span>
            <button
              type="button"
              className="epf-error__close"
              onClick={() => setError("")}
              aria-label={t("close")}
              title={t("close")}
            >
              <RiCloseLine />
            </button>
          </div>
        )}

        {/* ── Inline preview — rendered above the textarea when active ── */}
        {/* Preview shows the lowercased form — exactly what will be stored after posting */}
        {showPreview && content.trim() && (
          <div className="epf-preview-inline">
            <div className="epf-preview-inline-label">Önizleme</div>
            <div className="epf-preview-body">{renderPreview(lowercaseEntryContent(content))}</div>
          </div>
        )}

        {/* ── Textarea ── */}
        <ChipEditor
          ref={textareaRef}
          className={textareaExpanded ? "chip-editor--expanded" : undefined}
          onFocus={() => setTextareaExpanded(true)}
          value={content}
          onChange={(next) => { setContent(next); if (error) setError(""); }}
          placeholder={personId
            ? t("person_entry_placeholder", "Bu kişi hakkında yaz...")
            : clubId
            ? t("club_entry_placeholder", "Bu kulüp hakkında yaz...")
            : teamId
            ? t("team_entry_placeholder", "Bu takım hakkında yaz...")
            : customTopicId
            ? t("topic_entry_placeholder", "Bu başlık hakkında yaz...")
            : t("entry_form_placeholder", {
                homeTeam: selectedMatch?.homeTeam,
                awayTeam: selectedMatch?.awayTeam,
              })}
          disabled={isPosting || (!hasTarget)}
          maxLength={entryMaxLength}
        />

        {/* ── Bottom row: keyboard left, submit right ── */}
        <div className="epf-bottom-row">
          <div className="epf-keyboard-wrapper" ref={trKeyboardRef}>
            {/* Turkish keyboard toggle button */}
            <button
              type="button"
              className={`epf-keyboard-btn${showTrKeyboard ? " epf-keyboard-btn--active" : ""}`}
              title={t("turkish_char_keyboard", "Turkish character keyboard")}
              onClick={() => setShowTrKeyboard((prev) => !prev)}
              disabled={!hasTarget}
            >
              <MdKeyboard size={16} />
            </button>

            {/* Save-to-Drafts button — shown when there is content AND any topic target is active.
                Supports match, person, club, team, and custom topic drafts.
                Icon-only button — label is shown via the native title tooltip. */}
            {content.length > 0 && hasTarget && (
              <button
                type="button"
                className="epf-save-draft-btn"
                title={isSavingDraft ? t("entry_save_draft_saving") : t("entry_save_draft")}
                aria-label={t("entry_save_draft")}
                onClick={handleSaveDraft}
                disabled={isSavingDraft || isPosting}
              >
                <FaRegSave size={16} />
              </button>
            )}

            {/* Clear content button — only visible when there is something to clear.
                Hidden while the draft chip is shown, because the chip carries its
                own trash button that does exactly the same thing. */}
            {content.length > 0 && !draftRestored && (
              <button
                type="button"
                className="epf-clear-btn"
                title={t("admin_clear_filters", "Clear")}
                onClick={() => {
                  setContent("");
                  setShowPreview(false);
                  setTextareaExpanded(false);
                }}
              >
                <RiDeleteBin6Line size={16} />
              </button>
            )}

            {/* Draft-restored chip — sits in the bottom row, right of the clear
                button, so it never pushes the editor around */}
            {draftRestored && (
              <span className="epf-draft-chip">
                <FaRegSave size={12} className="epf-draft-chip-icon" />
                <span className="epf-draft-chip-label">{t("entry_draft_restored")}</span>
                <button
                  type="button"
                  className="epf-draft-discard"
                  title={t("entry_draft_discard")}
                  aria-label={t("entry_draft_discard")}
                  onClick={() => {
                    setContent("");
                    setDraftRestored(false);
                    setShowPreview(false);
                    setTextareaExpanded(false);
                    if (localDraftKey) localStorage.removeItem(localDraftKey);
                  }}
                >
                  <RiDeleteBin6Line size={13} />
                </button>
              </span>
            )}

            {/* Turkish character popup */}
            {showTrKeyboard && (
              <div className="epf-tr-keyboard">
                {TURKISH_CHARS.map((char) => (
                  <button
                    key={char}
                    type="button"
                    className="epf-tr-char-btn"
                    onClick={() => insertTurkishChar(char)}
                  >
                    {char}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Preview button */}
          <button
            type="button"
            className="epf-preview-btn"
            onClick={() => setShowPreview((prev) => !prev)}
            disabled={(!hasTarget) || !content.trim()}
          >
            Önizleme
          </button>

          {/* Submit button */}
          <button
            type="submit"
            className="epf-submit-btn"
            disabled={isPosting || isImageUploading || (!hasTarget) || !content.trim()}
          >
            {isPosting ? t("entry_form_posting_button") : t("entry_form_post_button")}
          </button>
        </div>
      </form>



      {/* Rate limit toast */}
      {showRateLimitToast && (
        <div className="entry-rate-limit-toast">{t("entry_rate_limit_exceeded")}</div>
      )}

      {/* Draft saved confirmation toast */}
      {draftSavedToast && (
        <div className="entry-draft-saved-toast">{t("entry_draft_saved_toast")}</div>
      )}
    </div>
  );
};